import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStatus, PerfumeResponse } from "../../types/types";
import { fetchCart } from "./cart-thunks";

export interface CartState {
loadingState: LoadingStatus;
totalPrice: number;       // Total payable (after discounts)
  totalDiscount: number;    // Total discount amount
  cartItemsCount: number;
  perfumes: Array<PerfumeResponse>;
}

export const initialState: CartState = {
  loadingState: LoadingStatus.LOADING,
  totalPrice: 0,
  totalDiscount: 0,
  cartItemsCount: 0,
  perfumes: [],
};

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    calculateCartPrice(state, action: PayloadAction<Array<PerfumeResponse>>) {
      const { total, discount } = calculatePrice(action.payload);
      state.totalPrice = total;
      state.totalDiscount = discount;
      state.loadingState = LoadingStatus.LOADED;
    },
    removePerfumeById(state, action: PayloadAction<number>) {
      const perfumes = state.perfumes.filter((p) => p.id !== action.payload);
      const { total, discount } = calculatePrice(perfumes);
      state.perfumes = perfumes;
      state.totalPrice = total;
      state.totalDiscount = discount;
      state.loadingState = LoadingStatus.LOADED;
    },
    setCartItemsCount(state, action: PayloadAction<number>) {
      state.cartItemsCount = action.payload;
    },
    resetCartState(state) {
      state.loadingState = LoadingStatus.LOADING;
      state.totalPrice = 0;
      state.totalDiscount = 0;
      state.cartItemsCount = 0;
      state.perfumes = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCart.pending, (state) => {
      state.loadingState = LoadingStatus.LOADING;
    });
    builder.addCase(fetchCart.fulfilled, (state, action) => {
      const { total, discount } = calculatePrice(action.payload);
      state.totalPrice = total;
      state.totalDiscount = discount;
      state.cartItemsCount = action.payload.length;
      state.perfumes = action.payload;
      state.loadingState = LoadingStatus.LOADED;
    });
  },
});

export const {
  calculateCartPrice,
  removePerfumeById,
  setCartItemsCount,
  resetCartState,
} = cartSlice.actions;

export default cartSlice.reducer;

/**
 * ✅ Calculate totals using backend fields:
 *  - actualPrice → original MRP
 *  - price → final discounted price
 *  - discount → percentage (for info only)
 */
const calculatePrice = (
  perfumes: Array<PerfumeResponse>
): { total: number; discount: number } => {
  const perfumesFromLocalStorage: Map<number, number> = new Map(
    JSON.parse(localStorage.getItem("perfumes") as string)
  );

  const isDistributor = localStorage.getItem("role") === "3";  // ← add this

  let total = 0;
  let totalDiscount = 0;

  perfumesFromLocalStorage.forEach((qty, id) => {
    const perfume = perfumes.find((p) => p.id === id);
    if (perfume) {
      if (isDistributor) {
        // ← distributor: use distributorPrice, MRP is actualPrice
        const base       = perfume.actualPrice ?? perfume.price;
        const discounted = perfume.distributorPrice ?? perfume.price;
        total         += discounted * qty;
        totalDiscount += (base - discounted) * qty;
      } else {
        // ← retailer/user: existing logic unchanged
        const base       = perfume.actualPrice ?? perfume.price;
        const discounted = perfume.price ?? base;
        total         += discounted * qty;
        totalDiscount += (base - discounted) * qty;
      }
    }
  });

  return { total, discount: totalDiscount };
};
